import { useEffect, useState } from "react";
import { ReactComponent as FilterBy } from "assets/images/svgs/filterBy.svg";
import { FiSearch } from "react-icons/fi";
import FilterDrawer from "components/drawers/host-side/FilterDrawer";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "utils/helpers/enums/routes";
import Breadcrumb, { BreadcrumbLink } from "components/breadcrumbs";
import useHost from "../useHooks";
import {
  EVENT_STATUS,
  EVENT_TIMEFRAMES,
} from "utils/helpers/enums/shared.enum";
import { HostEventsFilterDTO } from "utils/helpers/models/host-dtos/events-filter.dto";
import { useDebounce } from "hooks/useDebounce";
import { EventDTO } from "utils/helpers/models/shared/event.dto";
import EventCard from "components/cards/event-card";
import { useSelector } from "react-redux";
import Pagination from "components/pagination";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import useStore from "hooks/useStore";
import EventNotFoundGif from "assets/images/gifs/event-not-found.gif";

const Experiences = () => {
  const breadcrumbLinks: BreadcrumbLink[] = [
    { title: "Home", path: siteRoutes.home },
    {
      title: "Experiences",
      path: siteRoutes.experiences,
    },
  ];
  const navigate = useNavigate();
  const [filters, setFilters] = useState<HostEventsFilterDTO>(() => {
    const defaultFilters = new HostEventsFilterDTO();
    // Set default values for proper tab behavior
    defaultFilters.timeFrame = EVENT_TIMEFRAMES.UPCOMING;
    defaultFilters.status = EVENT_STATUS.ALL;
    return defaultFilters;
  });

  const { isProfileCompleted } = useStore();

    // Check profile completion on mount - redirect to home if not completed
    useEffect(() => {
      const profileCompleted = isProfileCompleted();
      if (!profileCompleted) {
        navigate(siteRoutes.home, { replace: true });
      }
    }, [isProfileCompleted, navigate]);

  const { getEvents } = useHost();
  const debounceSearch = useDebounce(filters.searchTitle, 1000);
  const [data, setData] = useState<EventDTO[]>([]);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [appliedFilters, setAppliedFilters] = useState<any>({});
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [selectedTab, setSelectedTab] = useState<string>("upcoming"); // Track which tab is selected
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  // Combined tabs - only one can be selected at a time
  const allFilterTabs = [
    {
      title: "Upcoming",
      value: EVENT_TIMEFRAMES.UPCOMING,
      type: "timeFrame",
      id: "upcoming",
    },
    {
      title: "Past",
      value: EVENT_TIMEFRAMES.PAST,
      type: "timeFrame",
      id: "past",
    },
    { title: "Saved", value: EVENT_STATUS.DRAFT, type: "status", id: "saved" },
    {
      title: "Cancelled",
      value: EVENT_STATUS.CANCELLED,
      type: "status",
      id: "cancelled",
    },
  ];

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const getAllEvents = (filters: HostEventsFilterDTO, shouldClearData: boolean = true) => {
    // Clear data immediately when filtering/searching to show loading state
    if (shouldClearData) {
      setData([]);
    }

    const queryParams: any = {};
    for (let key in filters) {
      if (
        filters[key]?.toString() &&
        filters[key] !== EVENT_TIMEFRAMES.ALL &&
        filters[key] !== EVENT_STATUS.ALL &&
        filters[key] !== ""
      ) {
        queryParams[key] = filters[key];
      }
    }

    getEvents(setData, queryParams, [], setFilters);

    // Mark initial load as complete after first API call
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  };

  useEffect(() => {
    getAllEvents(filters);
  }, [debounceSearch]);

  // Load initial data on component mount
  useEffect(() => {
    getAllEvents(filters);
  }, []);

  const handleFilterChange = (event: any) => {
    const { name, value } = event.target;
    filters[name] = value;
    setFilters({ ...filters });
    if (name !== "searchTitle") {
      getAllEvents(filters);
    }
  };

  const handleTabChange = (
    filterName: string,
    value: string,
    tabId: string
  ) => {
    // Reset data immediately to show loading state
    setData([]);

    const newFilters = { ...filters };

    // Reset all tab-related filters
    // When selecting a status tab, exclude timeFrame from query
    // When selecting a timeFrame tab, exclude status from query
    if (filterName === "status") {
      // Status tab selected - remove timeFrame so it's not included in query
      newFilters.status = value as EVENT_STATUS;
      delete (newFilters as any).timeFrame;
    } else if (filterName === "timeFrame") {
      // TimeFrame tab selected - remove status so it's not included in query
      (newFilters as any).timeFrame = value;
      delete (newFilters as any).status;
    }
    
    newFilters.isSession = "";

    // Reset pagination when changing tabs
    newFilters.page = 0;
    newFilters.size = 12;

    // Update selected tab
    setSelectedTab(tabId);

    setFilters(newFilters);
    getAllEvents(newFilters);
  };

  const onApplyDrawerFilters = (drawerFilters: any) => {
    const newFilters = { ...filters };
    const newAppliedFilters: any = {};

    for (let key in drawerFilters) {
      if (drawerFilters[key] && drawerFilters[key] !== "") {
        newFilters[key] = drawerFilters[key];
        newAppliedFilters[key] = drawerFilters[key];
      } else {
        delete newFilters[key];
        delete newAppliedFilters[key];
      }
    }

    setAppliedFilters(newAppliedFilters);
    setFilters(newFilters);
    getAllEvents(newFilters);
  };

  const removeAppliedFilter = (filterKey: string) => {
    const newFilters = { ...filters };
    const newAppliedFilters = { ...appliedFilters };

    delete newFilters[filterKey];
    delete newAppliedFilters[filterKey];

    setAppliedFilters(newAppliedFilters);
    setFilters(newFilters);
    getAllEvents(newFilters);
  };

  const goToExpDetail = (id: number) => {
    navigate(`${siteRoutes.experienceDetail}?id=${id}`);
  };

  const handlePageChange = (pageInfo: { selected: number; limit: number }) => {
    filters.page = pageInfo.selected;
    filters.size = pageInfo.limit;
    getAllEvents(filters);
  };

  // Check if a tab is currently active
  const isTabActive = (tab: any) => {
    return selectedTab === tab.id;
  };

  return (
    <div className="container min-h-screen mb-4">
      <Breadcrumb links={breadcrumbLinks} />
      <h2 className="text-2xl text-blackie font-bold my-4">Experiences</h2>
      <div className="flex justify-between  flex-wrap items-center gap-5">
        <div className="flex flex-wrap gap-5 items-center">
          <div className="flex space-x-4 md:space-x-10 pb-2">
            {allFilterTabs.map((tab, index) => (
              <button
                key={index}
                onClick={() => handleTabChange(tab.type, tab.value, tab.id)}
                className={`text-base font-semibold pb-1 flex items-center ${
                  isTabActive(tab)
                    ? "text-yellowText border-b-2 border-yellowText"
                    : "text-grayText hover:text-yellowText"
                }`}
              >
                {tab.title}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="search"
              name="searchTitle"
              placeholder="Find Experience.."
              value={filters.searchTitle}
              onChange={handleFilterChange}
              className="w-full pl-10 pr-4 py-2 h-[51px] rounded-lg border border-inputsBorderColor outline-none"
            />
          </div>
          <button
            onClick={toggleDrawer}
            className="text-labelTextColor flex gap-1 font-medium"
          >
            Filter by <FilterBy />
          </button>

          {/* Applied Filters Display */}
          {Object.keys(appliedFilters).length > 0 && (
            <div className="flex flex-wrap gap-2 items-center">
              {/* <span className="text-sm text-grayText">Applied:</span> */}
              {Object.entries(appliedFilters).map(([key, value]) => {
                const getDisplayLabel = (key: string, value: any) => {
                  if (key === "isSession") {
                    return value === "true" ? "Sessions" : "Workshops";
                  }
                  if (key === "ageGroups") {
                    return `${value}`.replace(/_/g, " ");
                  }
                  return `${key}: ${value}`;
                };

                return (
                  <div
                    key={key}
                    className="flex items-center gap-1 bg-yellowText/10 text-yellowText px-3 py-1 rounded-full text-sm"
                  >
                    <span className="capitalize">
                      {getDisplayLabel(key, value)}
                    </span>
                    <button
                      onClick={() => removeAppliedFilter(key)}
                      className="text-red-500 hover:text-red-700 ml-1"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 my-6">
        {data.map((event, index) => (
          <EventCard event={event} key={index} />
        ))}
      </div>

      <CustomMessageDisplay
        show={!isLoading && !data.length && !isInitialLoad}
        title={
          selectedTab === "upcoming"
            ? "No Upcoming Experiences"
            : selectedTab === "past"
            ? "No Past Experiences"
            : selectedTab === "saved"
            ? "No Saved Experiences"
            : "No Cancelled Experiences"
        }
        slogan={
          selectedTab === "upcoming"
            ? "Create your upcoming experience to get started!"
            : selectedTab === "past"
            ? "Your completed experiences will appear here"
            : selectedTab === "saved"
            ? "Your draft experiences will be shown here"
            : "Cancelled experiences will be displayed here"
        }
        image={EventNotFoundGif}
        imageAlt="No experiences found"
        className="py-12"
        imageClassName="w-[150px] h-[120px]"
        titleClassName="text-2xl font-bold text-blackie"
        sloganClassName="text-gray-500 text-base"
      />

      {data?.length > 0 && (
        <Pagination
          count={filters.totalElements}
          page={filters.page}
          limit={filters.size}
          onPageChange={handlePageChange}
          activeBgColor={"#DE9C27"}
          activeTextColor={"#ffffff"}
        />
      )}
      {drawerOpen && (
        <FilterDrawer
          isOpen={drawerOpen}
          onClose={toggleDrawer}
          onApply={onApplyDrawerFilters}
          currentFilters={appliedFilters}
        />
      )}
    </div>
  );
};

export default Experiences;
